import React, { useState, useEffect } from 'react';
import { FirmAdminLayout } from './FirmAdminLayout';
import { api } from '../auth/authStore';
import {
  DollarSign,
  Plus,
  Search,
  Filter,
  Download,
  Send,
  Eye,
  MoreVertical,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  CreditCard,
  FileText,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { InvoiceCreateModal } from './components/InvoiceCreateModal';
import { InvoiceDetailsModal } from './components/InvoiceDetailsModal';
import { PaymentModal } from './components/PaymentModal';
import { InvoiceEditModal } from './components/InvoiceEditModal';

interface Invoice {
  _id: string;
  number: string;
  clientId: {
    _id: string;
    displayName: string;
    email: string;
  };
  issueDate: string;
  dueDate: string;
  currency: string;
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  totals: {
    subtotal: number;
    taxTotal: number;
    total: number;
    amountPaid: number;
    amountDue: number;
  };
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface InvoiceStats {
  total: number;
  byStatus: Record<string, number>;
  totalAmount: number;
  paidAmount: number;
  outstandingAmount: number;
  overdueAmount: number;
  averageAmount: number;
}

interface BillingOverview {
  summary: {
    totalInvoices: number;
    totalRevenue: number;
    outstandingAmount: number;
    overdueAmount: number;
    averageInvoiceValue: number;
  };
  recentInvoices: Array<{
    id: string;
    number: string;
    clientName: string;
    amount: number;
    status: string;
    dueDate: Date;
  }>;
  paymentTrends: Array<{
    month: string;
    invoiced: number;
    collected: number;
  }>;
}

export const BillingInvoicing: React.FC = () => {
  // State management
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [billingOverview, setBillingOverview] = useState<BillingOverview | null>(null);
  const [invoiceStats, setInvoiceStats] = useState<InvoiceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('issueDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Modal state
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // Bulk operations state
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchInvoices();
  }, [currentPage, itemsPerPage, statusFilter, sortBy, sortOrder, searchTerm, dateFilter]);

  useEffect(() => {
    fetchBillingOverview();
    fetchInvoiceStats();
  }, []);

  // API functions
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (dateFilter !== 'all') {
        const now = new Date();
        switch (dateFilter) {
          case 'today':
            params.fromDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
            params.toDate = new Date(now.setHours(23, 59, 59, 999)).toISOString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            params.fromDate = weekAgo.toISOString();
            break;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            params.fromDate = monthAgo.toISOString();
            break;
          case 'overdue':
            params.overdue = true;
            break;
        }
      }

      const response = await api.get('/firm-admin/billing/invoices', { params });
      setInvoices(response.data.data.invoices || []);
      setTotalItems(response.data.data.total || 0);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch invoices');
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingOverview = async () => {
    try {
      const response = await api.get('/firm-admin/billing/overview');
      setBillingOverview(response.data.data);
    } catch (error) {
      console.error('Failed to fetch billing overview:', error);
    }
  };

  const fetchInvoiceStats = async () => {
    try {
      const response = await api.get('/firm-admin/billing/invoices/stats');
      setInvoiceStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch invoice stats:', error);
    }
  };

  const handleRefresh = () => {
    fetchInvoices();
    fetchBillingOverview();
    fetchInvoiceStats();
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter: string, value: string) => {
    if (filter === 'status') {
      setStatusFilter(value);
    } else if (filter === 'date') {
      setDateFilter(value);
    }
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleInvoiceSelect = (invoiceId: string) => {
    setSelectedInvoices(prev =>
      prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map(invoice => invoice._id));
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedInvoices.length === 0) return;

    setBulkActionLoading(true);
    try {
      await api.post('/firm-admin/billing/invoices/bulk-action', {
        action,
        invoiceIds: selectedInvoices,
      });

      setSelectedInvoices([]);
      setShowBulkActions(false);
      await fetchInvoices();
      await fetchInvoiceStats();
    } catch (error: any) {
      setError(error.response?.data?.message || `Failed to ${action} invoices`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleViewInvoice = async (invoiceId: string) => {
    try {
      const response = await api.get(`/firm-admin/billing/invoices/${invoiceId}`);
      setSelectedInvoice(response.data.data);
      setSelectedInvoiceId(invoiceId);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      setError('Failed to load invoice details');
    }
  };

  const handleAddPayment = async (invoiceId: string) => {
    try {
      const response = await api.get(`/firm-admin/billing/invoices/${invoiceId}`);
      setSelectedInvoice(response.data.data);
      setSelectedInvoiceId(invoiceId);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      setError('Failed to load invoice details');
    }
  };

  const handleCreateInvoice = () => {
    setShowCreateModal(true);
  };

  const handleEditInvoice = async (invoiceId: string) => {
    try {
      const response = await api.get(`/firm-admin/billing/invoices/${invoiceId}`);
      setSelectedInvoice(response.data.data);
      setSelectedInvoiceId(invoiceId);
      setShowEditModal(true);
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      setError('Failed to load invoice details');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/firm-admin/billing/invoices/${invoiceId}`);
      await fetchInvoices();
      await fetchBillingOverview();
      await fetchInvoiceStats();
    } catch (error: any) {
      console.error('Failed to delete invoice:', error);
      setError(error.response?.data?.message || 'Failed to delete invoice');
    }
  };

  const handleSendInvoice = async (invoiceId: string) => {
    try {
      setError(''); // Clear any previous errors

      // Find the invoice to get client email and invoice number
      const invoice = invoices.find(inv => inv._id === invoiceId);
      if (!invoice) {
        setError('Invoice not found');
        return;
      }

      // Validate that we have required data
      if (!invoice.clientId?.contact?.email) {
        setError('Client email is required to send invoice');
        return;
      }

      console.log('Sending invoice:', {
        invoiceId,
        invoiceNumber: invoice.number,
        clientEmail: invoice.clientId.contact.email,
        status: invoice.status
      });

      const requestData = {
        email: invoice.clientId.contact.email,
        subject: `Invoice ${invoice.number}`,
        message: 'Please find your invoice attached.'
      };

      console.log('Request data:', requestData);

      const response = await api.post(`/firm-admin/billing/invoices/${invoiceId}/send`, requestData);

      console.log('Send invoice response:', response.data);

      // Refresh the data
      await fetchInvoices();
      await fetchInvoiceStats();

      // Show success message
      console.log('Invoice sent successfully');

    } catch (error: any) {
      console.error('Failed to send invoice:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });

      let errorMessage = 'Failed to send invoice';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    }
  };

  const handleExportInvoices = async () => {
    try {
      const params: any = {
        page: 1,
        limit: 1000, // Export all
        sortBy,
        sortOrder,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (dateFilter && dateFilter !== 'all') {
        const now = new Date();
        switch (dateFilter) {
          case 'today':
            params.fromDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
            params.toDate = new Date(now.setHours(23, 59, 59, 999)).toISOString();
            break;
          case 'week':
            const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
            params.fromDate = weekStart.toISOString();
            break;
          case 'month':
            params.fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
            break;
          case 'overdue':
            params.overdue = true;
            break;
        }
      }

      const response = await api.get('/firm-admin/billing/invoices', { params });
      const invoices = response.data.data.invoices || [];

      // Create CSV content
      const headers = ['Invoice Number', 'Client', 'Amount', 'Status', 'Issue Date', 'Due Date'];
      const csvContent = [
        headers.join(','),
        ...invoices.map((invoice: Invoice) => [
          invoice.number,
          `"${invoice.clientId.displayName}"`,
          invoice.totals.total,
          invoice.status,
          new Date(invoice.issueDate).toLocaleDateString(),
          new Date(invoice.dueDate).toLocaleDateString(),
        ].join(','))
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to export invoices:', error);
      setError(error.response?.data?.message || 'Failed to export invoices');
    }
  };

  const handleModalSuccess = () => {
    fetchInvoices();
    fetchBillingOverview();
    fetchInvoiceStats();
  };

  // Utility functions
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <FileText className="w-4 h-4 text-gray-500" />;
      case 'sent':
        return <Send className="w-4 h-4 text-blue-500" />;
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'cancelled':
        return <X className="w-4 h-4 text-gray-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSendButtonStyle = (status: string) => {
    const isDraft = status === 'draft';
    return {
      color: isDraft ? '#2563eb' : '#d1d5db',
      backgroundColor: isDraft ? '#e0f2fe' : '#f9fafb', // Light blue background for draft
      opacity: isDraft ? 1 : 0.5,
      cursor: isDraft ? 'pointer' : 'not-allowed',
      border: isDraft ? '2px solid #2563eb' : '2px solid #d1d5db', // Border to make it more obvious
      padding: '4px',
      borderRadius: '4px',
      transition: 'all 0.2s'
    };
  };

  const getPaymentButtonStyle = (status: string) => {
    const isEnabled = status === 'sent' || status === 'overdue' || status === 'paid';
    const isDisabled = status === 'draft' || status === 'cancelled';
    return {
      color: isEnabled ? '#16a34a' : '#d1d5db',
      backgroundColor: isEnabled ? '#f0fdf4' : '#f9fafb', // Light green background for enabled
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      border: isEnabled ? '2px solid #16a34a' : '2px solid #d1d5db', // Border to make it more obvious
      padding: '4px',
      borderRadius: '4px',
      transition: 'all 0.2s'
    };
  };

  const formatCurrency = (amount: number, currency: string = 'CAD') => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSelectedInvoice = () => {
    return invoices.find(invoice => invoice._id === selectedInvoiceId);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <FirmAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Billing & Invoicing</h1>
              <p className="text-gray-600">Manage invoices, track payments, and monitor revenue</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRefresh}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={handleCreateInvoice}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Invoice
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {billingOverview ? formatCurrency(billingOverview.summary.totalRevenue) : '$0.00'}
                </p>
              </div>
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Outstanding</p>
                <p className="text-2xl font-bold text-gray-900">
                  {billingOverview ? formatCurrency(billingOverview.summary.outstandingAmount) : '$0.00'}
                </p>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Clock className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-red-600">
                  {billingOverview ? formatCurrency(billingOverview.summary.overdueAmount) : '$0.00'}
                </p>
              </div>
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Invoices</p>
                <p className="text-2xl font-bold text-gray-900">
                  {billingOverview ? billingOverview.summary.totalInvoices : 0}
                </p>
              </div>
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <FileText className="w-4 h-4 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Filters and Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by invoice number or client name..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Date Filter */}
            <div className="relative">
              <select
                value={dateFilter}
                onChange={(e) => handleFilterChange('date', e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="overdue">Overdue Only</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleExportInvoices}
                className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedInvoices.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-700">
                  {selectedInvoices.length} invoice{selectedInvoices.length > 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkAction('send')}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 disabled:opacity-50"
                  >
                    Send
                  </button>
                  <button
                    onClick={() => handleBulkAction('delete')}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 disabled:opacity-50"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setSelectedInvoices([])}
                    className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <FileText className="w-12 h-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
                        <p className="text-gray-500 mb-4">
                          {searchTerm || statusFilter !== 'all' || dateFilter !== 'all'
                            ? 'Try adjusting your filters to see more results.'
                            : 'Get started by creating your first invoice.'}
                        </p>
                        {!searchTerm && statusFilter === 'all' && dateFilter === 'all' && (
                          <button
                            onClick={handleCreateInvoice}
                            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Your First Invoice
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                  <tr key={invoice._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice._id)}
                        onChange={() => handleInvoiceSelect(invoice._id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{invoice.number}</div>
                        <div className="text-sm text-gray-500">{invoice.items[0]?.description || 'No description'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{invoice.clientId.displayName}</div>
                      <div className="text-sm text-gray-500">Created: {new Date(invoice.createdAt).toLocaleDateString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">${invoice.totals.total.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(invoice.status)}
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </div>

                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{new Date(invoice.dueDate).toLocaleDateString()}</div>
                      {invoice.status === 'overdue' && (
                        <div className="text-xs text-red-600">
                          {Math.ceil((new Date().getTime() - new Date(invoice.dueDate).getTime()) / (1000 * 3600 * 24))} days overdue
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewInvoice(invoice._id)}
                          className="p-2 rounded text-white bg-gray-600 border-2 border-gray-800 hover:bg-gray-700 cursor-pointer font-bold"
                          title="View Details"
                          style={{
                            backgroundColor: '#4b5563',
                            color: 'white',
                            border: '2px solid #374151'
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {/* Send Button */}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            console.log('Send Button Debug:', {
                              status: invoice.status,
                              isDraft: invoice.status === 'draft',
                              statusType: typeof invoice.status,
                              statusLength: invoice.status?.length,
                              statusTrimmed: invoice.status?.trim(),
                              invoiceId: invoice._id
                            });
                            if (invoice.status === 'DRAFT') {
                              handleSendInvoice(invoice._id);
                            }
                          }}
                          disabled={invoice.status !== 'DRAFT'}
                          className={invoice.status === 'DRAFT'
                            ? "p-2 rounded text-white bg-blue-600 border-2 border-blue-800 hover:bg-blue-700 cursor-pointer font-bold"
                            : "p-2 rounded text-gray-400 bg-gray-100 border-2 border-gray-300 opacity-30 cursor-not-allowed"
                          }
                          title={invoice.status === 'DRAFT' ? "Send Invoice" : "Invoice already sent"}
                          style={{
                            backgroundColor: invoice.status === 'DRAFT' ? '#2563eb' : '#f3f4f6',
                            color: invoice.status === 'DRAFT' ? 'white' : '#9ca3af',
                            border: invoice.status === 'DRAFT' ? '2px solid #1e40af' : '2px solid #d1d5db'
                          }}
                        >
                          <Send className="w-4 h-4" />
                        </button>

                        {/* Add Payment Button */}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            const isPaymentEnabled = invoice.status === 'SENT' || invoice.status === 'OVERDUE';
                            console.log('Payment Button Debug:', {
                              status: invoice.status,
                              isPaymentEnabled,
                              isSent: invoice.status === 'SENT',
                              isOverdue: invoice.status === 'OVERDUE',
                              isPaid: invoice.status === 'paid',
                              statusType: typeof invoice.status,
                              invoiceId: invoice._id
                            });

                            if (isPaymentEnabled) {
                              // For SENT and OVERDUE invoices, allow adding payments
                              handleAddPayment(invoice._id);
                            }
                          }}
                          disabled={!(invoice.status === 'SENT' || invoice.status === 'OVERDUE')}
                          className={(invoice.status === 'SENT' || invoice.status === 'OVERDUE')
                            ? "p-2 rounded text-white bg-green-600 border-2 border-green-800 hover:bg-green-700 cursor-pointer font-bold"
                            : "p-2 rounded text-gray-400 bg-gray-100 border-2 border-gray-300 opacity-30 cursor-not-allowed"
                          }
                          title={
                            invoice.status === 'SENT' || invoice.status === 'OVERDUE'
                              ? "Add Payment"
                              : invoice.status === 'PAID'
                              ? "Invoice is fully paid"
                              : "Send invoice first to add payments"
                          }
                          style={{
                            backgroundColor: (invoice.status === 'SENT' || invoice.status === 'OVERDUE')
                              ? '#16a34a'
                              : '#f3f4f6',
                            color: (invoice.status === 'SENT' || invoice.status === 'OVERDUE') ? 'white' : '#9ca3af',
                            border: (invoice.status === 'SENT' || invoice.status === 'OVERDUE')
                              ? '2px solid #15803d'
                              : '2px solid #d1d5db'
                          }}
                        >
                          <CreditCard className="w-4 h-4" />
                        </button>
                        <div className="relative group">
                          <button className="text-gray-400 hover:text-gray-600" title="More options">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 top-6 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                            <button
                              onClick={() => handleEditInvoice(invoice._id)}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Edit Invoice
                            </button>
                            <button
                              onClick={() => handleDeleteInvoice(invoice._id)}
                              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                            >
                              Delete Invoice
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-700">
                <span>
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm font-medium rounded-md ${
                        currentPage === pageNum
                          ? 'text-blue-600 bg-blue-50 border border-blue-300'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Modals */}
      <InvoiceCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleModalSuccess}
      />

      <InvoiceDetailsModal
        isOpen={showDetailsModal}
        invoiceId={selectedInvoiceId}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedInvoice(null);
          setSelectedInvoiceId(null);
        }}
        onUpdate={handleModalSuccess}
      />

      {selectedInvoice && (
        <PaymentModal
          isOpen={showPaymentModal}
          invoiceId={selectedInvoiceId}
          invoiceNumber={selectedInvoice.number}
          amountDue={selectedInvoice.totals?.amountDue || 0}
          currency={selectedInvoice.currency || 'CAD'}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedInvoice(null);
            setSelectedInvoiceId(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {selectedInvoice && (
        <InvoiceEditModal
          isOpen={showEditModal}
          invoice={selectedInvoice}
          onClose={() => {
            setShowEditModal(false);
            setSelectedInvoice(null);
            setSelectedInvoiceId(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}
    </FirmAdminLayout>
  );
};
